/**
 * Shadow/Elevation Design Tokens
 *
 * Standard shadow values for creating depth and elevation in the UI.
 * Based on Material Design elevation system with 5 levels.
 *
 * Usage:
 * - sx={{ boxShadow: tokens.shadows.md }}
 * - theme.tokens.shadows.card
 */

/**
 * Core shadow scale (0-5 elevation levels)
 */
export const shadows = {
  /** No shadow - flat surface (elevation 0) */
  none: 'none',

  /**
   * Extra small shadow - subtle depth (elevation 1)
   * Use for: hover states, slightly raised elements
   */
  xs: '0px 1px 2px rgba(0, 0, 0, 0.05)',

  /**
   * Small shadow - light elevation (elevation 2)
   * Use for: cards at rest, buttons, chips
   */
  sm: '0px 2px 4px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)',

  /**
   * Medium shadow - moderate elevation (elevation 3)
   * Use for: raised cards, dropdowns, popovers
   */
  md: '0px 4px 8px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.06)',

  /**
   * Large shadow - high elevation (elevation 4)
   * Use for: modals, drawers, tooltips
   */
  lg: '0px 8px 16px rgba(0, 0, 0, 0.12), 0px 4px 8px rgba(0, 0, 0, 0.08)',

  /**
   * Extra large shadow - highest elevation (elevation 5)
   * Use for: dialogs, full-screen overlays, sticky headers
   */
  xl: '0px 12px 24px rgba(0, 0, 0, 0.15), 0px 6px 12px rgba(0, 0, 0, 0.1)',

  /**
   * 2X large shadow - maximum elevation (elevation 6)
   * Use for: prominent modals, alerts that need maximum attention
   */
  '2xl': '0px 16px 32px rgba(0, 0, 0, 0.18), 0px 8px 16px rgba(0, 0, 0, 0.12)',
} as const;

/**
 * Colored shadows for semantic elements
 */
export const coloredShadows = {
  /** Primary brand shadow with green tint */
  primary: '0px 4px 12px rgba(4, 98, 52, 0.2)',

  /** Success shadow with green tint */
  success: '0px 4px 12px rgba(88, 221, 94, 0.25)',

  /** Warning shadow with orange tint */
  warning: '0px 4px 12px rgba(255, 186, 23, 0.25)',

  /** Error shadow with red tint */
  error: '0px 4px 12px rgba(249, 44, 44, 0.25)',

  /** Info shadow with blue tint */
  info: '0px 4px 12px rgba(38, 154, 255, 0.25)',
} as const;

/**
 * Inner shadows for depth effects
 */
export const innerShadows = {
  /** Subtle inset shadow */
  sm: 'inset 0px 1px 2px rgba(0, 0, 0, 0.06)',

  /** Medium inset shadow */
  md: 'inset 0px 2px 4px rgba(0, 0, 0, 0.1)',

  /** Strong inset shadow */
  lg: 'inset 0px 4px 8px rgba(0, 0, 0, 0.15)',
} as const;

/**
 * Semantic shadow tokens for specific use cases
 * These provide preset shadows for common UI patterns
 */
export const semanticShadows = {
  /** Card shadow - default card elevation */
  card: shadows.sm,

  /** Card hover shadow - increased elevation on hover */
  cardHover: shadows.md,

  /** Button shadow - subtle depth for buttons */
  button: shadows.xs,

  /** Button hover shadow - increased depth on hover */
  buttonHover: shadows.sm,

  /** Dropdown/Menu shadow - floating menus */
  dropdown: shadows.md,

  /** Modal shadow - high elevation overlay */
  modal: shadows.lg,

  /** Tooltip shadow - floating helper text */
  tooltip: shadows.sm,

  /** Drawer shadow - side panel elevation */
  drawer: shadows.lg,

  /** AppBar shadow - fixed header */
  appBar: shadows.sm,

  /** Floating action button shadow */
  fab: shadows.md,

  /** Floating action button hover shadow */
  fabHover: shadows.lg,

  /** Input focus shadow */
  inputFocus: '0px 0px 0px 3px rgba(4, 98, 52, 0.1)',

  /** Paper shadow - default paper elevation */
  paper: shadows.sm,
} as const;

/**
 * Focus ring styles for accessibility
 */
export const focusRings = {
  /** Default focus ring - primary color */
  default: '0px 0px 0px 3px rgba(4, 98, 52, 0.15)',

  /** Error focus ring - error color */
  error: '0px 0px 0px 3px rgba(249, 44, 44, 0.15)',

  /** Strong focus ring - high contrast */
  strong: '0px 0px 0px 4px rgba(4, 98, 52, 0.25)',
} as const;

/**
 * Utility type exports
 */
export type ShadowKey = keyof typeof shadows;
export type ColoredShadowKey = keyof typeof coloredShadows;
export type InnerShadowKey = keyof typeof innerShadows;
export type SemanticShadowKey = keyof typeof semanticShadows;
export type FocusRingKey = keyof typeof focusRings;
